import i18n from 'i18n';

const createTranslator =
  (basePath: string) =>
  (key: string): string =>
    i18n.t(`${basePath}.${key}`, { ns: 'transportCloud' });

const tHeaders = createTranslator('table.headers');
const mainColumnHeaders = {
  lp: tHeaders('lp'),
  shippingLine: tHeaders('shippingLine'),
  containerType: tHeaders('containerType'),
  customer: tHeaders('customer'),
  job: tHeaders('job'),
  referenceNumbers: tHeaders('referenceNumbers'),
  bookingNumber: tHeaders('bookingNumber'),
  pinCode: tHeaders('pinCode'),
  tradeType: tHeaders('tradeType'),
  pickupLocation: tHeaders('pickupLocation'),
  pickupDate: tHeaders('pickupDate'),
  loadingDeliveryLocation: tHeaders('loadingDeliveryLocation'),
  loadingDeliveryDate: tHeaders('loadingDeliveryDate'),
  deliveryLocation: tHeaders('deliveryLocation'),
  deliveryDate: tHeaders('deliveryDate'),
  customsClearance: tHeaders('customsClearance'),
  weighing: tHeaders('weighing'),
  productGroup: tHeaders('productGroup'),
  loadDescription: tHeaders('loadDescription'),
  comment: tHeaders('comment'),
  containerStatus: tHeaders('containerStatus'),
  documents: tHeaders('documents'),
  offerTruckerRate: tHeaders('offerTruckerRate'),
  truckerRate: tHeaders('truckerRate'),
  containerNumber: tHeaders('containerNumber'),
  triangulation: tHeaders('triangulation'),
  trucker: tHeaders('trucker'),
  driverName: tHeaders('driverName'),
  driverDocumentNumber: tHeaders('driverDocumentNumber'),
  vehicleRegistrationNumber: tHeaders('vehicleRegistrationNumber'),
  trailerRegistrationNumber: tHeaders('trailerRegistrationNumber'),
  driverPhone: tHeaders('driverPhone'),
  reportNotes: tHeaders('reportNotes'),
  actions: tHeaders('actions'),
  shippingLineHaulage: tHeaders('shippingLineHaulage'),
};

export const MAIN_HEADERS = Object.entries(mainColumnHeaders);

const tDayNames = createTranslator('table.dayNames');
export const DAY_NAMES = {
  monday: tDayNames('monday'),
  tuesday: tDayNames('tuesday'),
  wednesday: tDayNames('wednesday'),
  thursday: tDayNames('thursday'),
  friday: tDayNames('friday'),
  saturday: tDayNames('saturday'),
  sunday: tDayNames('sunday'),
  noLoadingDeliveryDate: tDayNames('noLoadingDeliveryDate'),
};

const tCustoms = createTranslator('table.statuses.customsClearance');
export const CUSTOMS_CLEARANCE_STATUSES = {
  none: tCustoms('none'),
  import: {
    inProgress: tCustoms('import.inProgress'),
    done: tCustoms('import.done'),
  },
  export: {
    inProgress: tCustoms('export.inProgress'),
    done: tCustoms('export.done'),
  },
};

const tTriangulation = createTranslator('table.statuses.triangulation');
export const TRIANGULATION_STATUSES = {
  inApproval: tTriangulation('inApproval'),
  approved: tTriangulation('approved'),
  none: tTriangulation('none'),
  depot: tTriangulation('depot'),
};

const tValidations = createTranslator('table.validations');
export const VALIDATIONS = {
  validations: {
    containerNumber: tValidations('containerNumber'),
    containerType: tValidations('containerType'),
    loadingDeliveryDate: tValidations('loadingDeliveryDate'),
    pickupDate: tValidations('pickupDate'),
    deliveryDate: tValidations('deliveryDate'),
    shippingLine: tValidations('shippingLine'),
    truckerRateValue: tValidations('truckerRateValue'),
    truckerRateCurrency: tValidations('truckerRateCurrency'),
    truckerRateNegative: tValidations('truckerRateNegative'),
    referenceNumbers: tValidations('referenceNumbers'),
    comment: tValidations('comment'),
    bookingNumber: tValidations('bookingNumber'),
    driverName: tValidations('driverName'),
    driverDocumentNumber: tValidations('driverDocumentNumber'),
    driverPhone: tValidations('driverPhone'),
    vehicleRegistrationNumber: tValidations('vehicleRegistrationNumber'),
    trailerRegistrationNumber: tValidations('trailerRegistrationNumber'),
    reportNotes: tValidations('reportNotes'),
    loadDescription: tValidations('loadDescription'),
    pinCode: tValidations('pinCode'),
  },
};

const tRequired = createTranslator('table.required');
export const REQUIRED = {
  loadingDeliveryDate: tRequired('loadingDeliveryDate'),
  pickupDate: tRequired('pickupDate'),
  deliveryDate: tRequired('deliveryDate'),
  loadingDeliveryLocation: tRequired('loadingDeliveryLocation'),
  pickupLocation: tRequired('pickupLocation'),
  deliveryLocation: tRequired('deliveryLocation'),
};

const tFilters = createTranslator('table.filters');
export const FILTERS = {
  dateFrom: tFilters('dateFrom'),
  dateTo: tFilters('dateTo'),
  onlyMine: tFilters('onlyMine'),
};

const tWeighing = createTranslator('table.statuses.weighing');
export const WEIGHING_STATUSES = {
  atLoading: tWeighing('atLoading'),
  atPort: tWeighing('atPort'),
  otherAddress: tWeighing('otherAddress'),
};

const tButtons = createTranslator('table.buttons');
export const BUTTONS = {
  addEntry: tButtons('addEntry'),
  viewSettings: tButtons('viewSettings'),
  save: tButtons('save'),
  cancel: tButtons('cancel'),
  delete: tButtons('delete'),
  yes: tButtons('yes'),
  no: tButtons('no'),
  reload: tButtons('reload'),
  accept: tButtons('accept'),
  send: tButtons('send'),
  add: i18n.t('button.create', { ns: 'misc' }),
};

const tErrors = createTranslator('table.errors');
export const ERRORS = {
  systemError: tErrors('systemError'),
  capturedError: tErrors('capturedError'),
  capturedEditDisabled: tErrors('capturedEditDisabled'),
  elseModifiedError: tErrors('elseModifiedError'),
  deleteError: tErrors('deleteError'),
  deleteErrorDataIntegrityViolation: tErrors(
    'deleteErrorDataIntegrityViolation',
  ),
  importEditDisabled: tErrors('importEditDisabled'),
  exportEditDisabled: tErrors('exportEditDisabled'),
  noPrivilegeEditDisabled: tErrors('noPrivilegeEditDisabled'),
  noPrivilegeDeleteDisabled: tErrors('noPrivilegeDeleteDisabled'),
  noPrivilegeAddLoadDisabled: tErrors('noPrivilegeAddLoadDisabled'),
  containerNumberError: tErrors('containerNumberError'),
  mailSenderNoPermission: tErrors('mailSenderNoPermission'),
  mailSenderNoSelection: tErrors('mailSenderNoPermission'),
  mailSenderNotAllJobRows: tErrors('mailSenderNotAllJobRows'),
  mailSenderNotSameJob: tErrors('mailSenderNotSameJob'),
  mailSenderNoTrucker: tErrors('mailSenderNoTrucker'),
  mailSenderNotOwner: tErrors('mailSenderNotOwner'),
  driverAddError: tErrors('driverAddError'),
  driverUpdateError: tErrors('driverUpdateError'),
  driverDeleteErrorSingle: tErrors('driverDeleteErrorSingle'),
  driverDeleteErrorMultiple: tErrors('driverDeleteErrorMultiple'),
};

export const OVERLAYS = {
  dateChangeOverlay: (date: string) =>
    i18n.t('table.overlays.dateChangeOverlay', {
      ns: 'transportCloud',
      date,
    }),
  dateChangeAcceptedOverlay: (date: string) =>
    i18n.t('table.overlays.dateChangeAcceptedOverlay', {
      ns: 'transportCloud',
      date,
    }),
  deleteOverlay: i18n.t('table.overlays.deleteOverlay', {
    ns: 'transportCloud',
  }),
};

const tDialogs = createTranslator('table.dialogs');
export const DIALOGS = {
  multiEditingConfirmation: tDialogs('multiEditingConfirmation'),
  acceptMultiEditingConfirmation: tDialogs('acceptMultiEditingConfirmation'),
  multiEditingCheck: tDialogs('multiEditingCheck'),
  confirmDelete: tDialogs('confirmDelete'),
  viewSettingsHeader: tDialogs('viewSettingsHeader'),
  mailSenderHeader: (title: string) =>
    i18n.t('table.dialogs.mailSenderHeader', { ns: 'transportCloud', title }),
  waybillLabel: tDialogs('waybillLabel'),
  waybillSuccessMessage: tDialogs('waybillSuccessMessage'),
  notificationLabel: tDialogs('notificationLabel'),
  notificationSuccessMessage: tDialogs('notificationSuccessMessage'),
  toLabel: tDialogs('toLabel'),
  ccLabel: tDialogs('ccLabel'),
  bccLabel: tDialogs('bccLabel'),
  subjectLabel: tDialogs('subjectLabel'),
  headingOneButton: tDialogs('headingOneButton'),
  headingTwoButton: tDialogs('headingTwoButton'),
  headingThreeButton: tDialogs('headingThreeButton'),
  boldButton: tDialogs('boldButton'),
  italicButton: tDialogs('italicButton'),
  strikethroughButton: tDialogs('strikethroughButton'),
  orderedListButton: tDialogs('orderedListButton'),
  bulletListButton: tDialogs('bulletListButton'),
  addRowBeforeButton: tDialogs('addRowBeforeButton'),
  addRowAfterButton: tDialogs('addRowAfterButton'),
  deleteRowButton: tDialogs('deleteRowButton'),
  addColumnBeforeButton: tDialogs('addColumnBeforeButton'),
  addColumnAfterButton: tDialogs('addColumnAfterButton'),
  deleteColumnButton: tDialogs('deleteColumnButton'),
  mergeCellsButton: tDialogs('mergeCellsButton'),
  splitCellsButton: tDialogs('splitCellsButton'),
  cellBackgroundButton: tDialogs('cellBackgroundButton'),
  deleteTableButton: tDialogs('deleteTableButton'),
  notificationDisabledTooltip: tDialogs('notificationDisabledTooltip'),
  waybillDisabledTooltip: tDialogs('waybillDisabledTooltip'),
  cloudManagement: tDialogs('cloudManagement'),
  cloudManagementStatuses: tDialogs('cloudManagementStatuses'),
  cloudManagementDriversDatabase: tDialogs('cloudManagementDriversDatabase'),
  driverAddSuccessMessage: tDialogs('driverAddSuccessMessage'),
  driverAddErrorMessage: tDialogs('driverAddErrorMessage'),
  driverUpdateSuccessMessage: tDialogs('driverUpdateSuccessMessage'),
  driverDeleteSuccessMessageSingle: tDialogs(
    'driverDeleteSuccessMessageSingle',
  ),
  driverDeleteSuccessMessageMultiple: tDialogs(
    'driverDeleteSuccessMessageMultiple',
  ),
  driverAddConfirm: tDialogs('driverAddConfirm'),
  driverEditConfirm: tDialogs('driverEditConfirm'),
  hexLabel: tDialogs('hexLabel'),
  noColorTooltip: tDialogs('noColorTooltip'),
  statusAddSuccessMessage: tDialogs('statusAddSuccessMessage'),
  statusUpdateSuccessMessage: tDialogs('statusUpdateSuccessMessage'),
  statusDeleteSuccessMessageSingle: tDialogs(
    'statusDeleteSuccessMessageSingle',
  ),
  statusDeleteSuccessMessageMultiple: tDialogs(
    'statusDeleteSuccessMessageMultiple',
  ),
};

const EDITABLE_COLUMNS = [
  'shippingLine',
  'containerType',
  'loadingDeliveryLocation',
  'pickupLocation',
  'deliveryLocation',
  'bookingNumber',
  'trailerRegistrationNumber',
  'vehicleRegistrationNumber',
  'driverPhone',
  'driverDocumentNumber',
  'driverName',
  'containerNumber',
  'productGroup',
  'truckerRate',
  'referenceNumbers',
  'comment',
  'pickupDate',
  'loadingDeliveryDate',
  'deliveryDate',
  'trucker',
  'customsClearance',
  'reportNotes',
  'triangulation',
  'loadDescription',
  'weighing',
  'pinCode',
  'shippingLineHaulage',
  'containerStatus',
];

const SHIPMENT_PLAN_EDITABLE_COLUMNS = [
  'shippingLine',
  'containerType',
  'tradeType',
  'loadingDeliveryLocation',
  'loadingDeliveryDate',
  'loadDescription',
  'comment',
  'truckerRate',
  'containerNumber',
  'trucker',
  'driverName',
  'driverDocumentNumber',
  'driverPhone',
];

const COLUMN_TO_FIELD_MAP: Record<string, string> = {
  bookingNumber: 'bookingNumber',
  containerNumber: 'containerNumber',
  triangulation: 'triangulation',
  comment: 'comment',
  trailerRegistrationNumber: 'trailerRegistrationNumber',
  vehicleRegistrationNumber: 'vehicleRegistrationNumber',
  driverPhone: 'driverPhone',
  driverDocumentNumber: 'driverDocumentNumber',
  driverName: 'driverName',
  productGroup: 'productGroup',
  loadDescription: 'loadDescription',
  referenceNumbers: 'combinedReferenceNumbers',
  shippingLine: 'shippingLineCode',
  containerType: 'containerType',
  loadingDeliveryLocation: 'loadingDeliveryLocationAddress',
  pickupLocation: 'pickupLocationDescription',
  deliveryLocation: 'deliveryLocationDescription',
  trucker: 'truckerCode',
  pickupDate: 'pickupDate',
  loadingDeliveryDate: 'loadingDeliveryDate',
  deliveryDate: 'deliveryDate',
  customsClearance: 'customsClearance',
  weighing: 'weighing',
  truckerRate: 'truckerRate',
  reportNotes: 'reportNotes',
  pinCode: 'pinCode',
  shippingLineHaulage: 'shippingLineHaulage',
  containerStatus: 'statusContainer',
  customer: 'customerCode',
  tradeType: 'tradeType',
};

const FIELD_TO_COLUMN_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(COLUMN_TO_FIELD_MAP).map(([feKey, beField]) => [
    beField,
    feKey,
  ]),
);

const MULTI_EDIT_FIELDS = [
  'loadingDeliveryDate',
  'productGroup',
  'loadDescription',
  'truckerRate',
  'triangulation',
  'customsClearance',
  'weighing',
  'pickupLocation',
  'trucker',
  'deliveryDate',
  'containerStatus',
];

const DATE_COLUMNS = ['pickupDate', 'loadingDeliveryDate', 'deliveryDate'];

const DRIVER_FIELDS = [
  'driverName',
  'driverDocumentNumber',
  'driverPhone',
] as const;

const OBJECT_VALUE_COLUMNS = [
  'shippingLine',
  'containerType',
  'loadingDeliveryLocation',
  'pickupLocation',
  'deliveryLocation',
  'trucker',
  'productGroup',
  'customer',
];

const FORWARDER_ONLY_COLUMNS = new Set([
  'shippingLine',
  'containerType',
  'referenceNumbers',
  'bookingNumber',
  'pinCode',
  'customsClearance',
  'weighing',
  'shippingLineHaulage',
]);

const TRANSPORT_ONLY_COLUMNS = new Set([
  'truckerRate',
  'containerNumber',
  'trucker',
  'driverName',
  'driverDocumentNumber',
  'driverPhone',
  'vehicleRegistrationNumber',
  'trailerRegistrationNumber',
  'reportNotes',
  'triangulation',
  'loadDescription',
  'productGroup',
]);

const SHIPPING_LINE_TRANSPORT_FORWARDER_COLUMNS = new Set([
  'truckerRate',
  'trucker',
  'driverName',
  'driverDocumentNumber',
  'driverPhone',
  'vehicleRegistrationNumber',
  'trailerRegistrationNumber',
]);

export const TIMING = {
  VALIDATION_FADE_START_DELAY: 10000,
  STATUS_FADE_START_DELAY: 1000,
  STATUS_FADE_OUT_DURATION: 1000,
  RECENTLY_EDITED_DURATION: 3000,
  PENDING_EDIT_TIMEOUT: 30000,
  BACKEND_RESPONSE_TIMEOUT: 30000,
} as const;

const DISPLAY_DATETIME_FORMAT = 'dd-MM-yyyy HH:mm';
const DISPLAY_DATE_FORMAT = 'dd-MM-yyyy';

const PAGINATION_ROWS = [10, 25, 50, 100, 250, 500];

export {
  mainColumnHeaders,
  PAGINATION_ROWS,
  EDITABLE_COLUMNS,
  SHIPMENT_PLAN_EDITABLE_COLUMNS,
  COLUMN_TO_FIELD_MAP,
  FIELD_TO_COLUMN_MAP,
  DATE_COLUMNS,
  OBJECT_VALUE_COLUMNS,
  FORWARDER_ONLY_COLUMNS,
  TRANSPORT_ONLY_COLUMNS,
  DISPLAY_DATETIME_FORMAT,
  DISPLAY_DATE_FORMAT,
  MULTI_EDIT_FIELDS,
  DRIVER_FIELDS,
  SHIPPING_LINE_TRANSPORT_FORWARDER_COLUMNS,
};
